import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { endpoints } from '@rossum/api-client';
import { api } from '../../../../lib/apiClient';

const QUERY_KEY_DOCUMENT_PAGE_CONTENT = 'document-page-content';
const ONE_HOUR = 1000 * 60 * 60;

const getBase64Image = (imgUrl: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      try {
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas
          .toDataURL('image/png')
          .replace(/^data:image\/png;base64,/, '');
        resolve(`data:image/png;base64,${dataURL}`);
      } catch {
        reject(new Error('Failed to convert image to Base64.'));
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image.'));
    };

    img.setAttribute('crossOrigin', 'anonymous');
    img.src = imgUrl;
  });

/**
 * Fetches page content and returns a displayable image URL.
 *
 * Returns a blob URL immediately so the page renders as soon as the
 * fetch completes. A background effect then converts to base64 (for
 * Smartlook session replay) and swaps the cache entry transparently.
 */
export const usePageContent = (
  contentUrl: string,
  pageId: number,
  options: { enabled: boolean }
) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => [QUERY_KEY_DOCUMENT_PAGE_CONTENT, pageId, contentUrl],
    [contentUrl, pageId]
  );
  const abortRef = useRef<AbortController>();

  const query = useQuery({
    queryKey,

    queryFn: async () => {
      const response = await api.request(endpoints.pages.getContent(pageId));
      return URL.createObjectURL(response);
    },

    ...options,
    staleTime: ONE_HOUR,
  });

  const blobUrl = query.data;

  useEffect(() => {
    if (!blobUrl || blobUrl.startsWith('data:')) return undefined;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    getBase64Image(blobUrl)
      .then(base64 => {
        if (controller.signal.aborted) return;
        queryClient.setQueryData(queryKey, base64);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {});

    return () => {
      controller.abort();
    };
  }, [blobUrl, queryClient, queryKey]);

  return query;
};
